import { useEffect } from 'react';
import { CATEGORY_MAPPING } from 'src/data';
import { useMapStore } from 'src/stores';
import { useAuth } from 'src/utils';

export function PoiMarkersLayer({ setLoadingFeatures, onMarkerClick }) {
  const { mapInstance } = useMapStore();
  const { user } = useAuth();

  useEffect(() => {
    if (!mapInstance) return;

    const map = mapInstance;

    const loadEverything = async () => {
      // --- Load icons
      for (const { symbol } of Object.values(CATEGORY_MAPPING)) {
        if (!map.hasImage(symbol)) {
          await new Promise((resolve) => {
            map.loadImage(`/icons/${symbol}.png`, (error, image) => {
              if (!error && image) {
                if (!map.hasImage(symbol)) map.addImage(symbol, image);
              }
              resolve();
            });
          });
        }
      }

      // --- Load GeoJSON
      const res = await fetch('/map-features.json');
      const geojson = await res.json();

      const filteredGeoJSON = {
        ...geojson,
        features: geojson.features.filter(
          f => user?.role === 'Admin' || f.properties.category !== 'Hidden Gems'
        )
      };

      if (!map.getSource('panam-pins')) {
        map.addSource('panam-pins', {
          type: 'geojson',
          data: filteredGeoJSON,
        });
      }

      if (!map.getLayer('panam-symbols')) {
        map.addLayer({
          id: 'panam-symbols',
          type: 'symbol',
          source: 'panam-pins',
          layout: {
            'icon-image': [
              'case',
              ...Object.entries(CATEGORY_MAPPING).flatMap(([category, { symbol }]) => [
                ['==', ['get', 'category'], category],
                symbol
              ]),
              'attraction',
            ],
            'icon-size': 0.5,
            'icon-allow-overlap': true,
          }
        });
      }

      // --- Cursor + Click
      map.on('mouseenter', 'panam-symbols', () => map.getCanvas().style.cursor = 'pointer');
      map.on('mouseleave', 'panam-symbols', () => map.getCanvas().style.cursor = '');

      map.on('click', 'panam-symbols', (e) => {
        const feature = e.features[0];
        const props = feature.properties;
        const coords = feature.geometry.coordinates;

        let parsedImages = props.images;
        try {
          parsedImages = typeof props.images === 'string'
            ? JSON.parse(props.images)
            : props.images;
        } catch {
          parsedImages = [];
        }

        onMarkerClick?.({
          id: props.id,
          title: props.title,
          description: props.description || '',
          coords,
          category: props.category,
          googleMapsUrl: props.googleMapsUrl,
          country: props.country,
          images: parsedImages,
        });
      });

      // Ensure loading done only after map fully renders layers
      map.once('idle', () => setLoadingFeatures(false));
    };

    const run = async () => {
      // 1) If map is already loaded, run immediately
      if (map.isStyleLoaded()) {
        await loadEverything();
      } else {
        // 2) Otherwise wait for Mapbox to load once
        map.once('load', loadEverything);
      }
    };

    run();
  }, [mapInstance, onMarkerClick, user?.role]);

  return null;
}